import React from "react";
import styled from "styled-components";
import { FlexColumn, THEME, Spinner } from "./OnboardingLayoutComponents";
import { BodyShortMedium } from "./OnboardingTypography";
import ClientOnboardingCard from "./ClientOnboardingCard";

// Styled components using FlexColumn instead of custom div
const ListContainer = styled(FlexColumn)`
  width: 100%;
  gap: ${THEME.spacing.lg};
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: ${THEME.spacing.ultra};
  color: ${THEME.colors.textMuted};
  background-color: ${THEME.colors.backgroundLight};
  border-radius: ${THEME.borderRadius.md};
  border: 1px solid ${THEME.colors.borderLight};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${THEME.spacing.lg};
  color: ${THEME.colors.borderDark};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${THEME.fontSize.lg};
  margin: 0 0 ${THEME.spacing.md} 0;
  color: ${THEME.colors.textSecondary};
`;

const EmptyStateMessage = styled.p`
  font-size: ${THEME.fontSize.md};
  margin: 0;
  color: ${THEME.colors.textMuted};
`;

const LoadingContainer = styled(FlexColumn)`
  align-items: center;
  padding: ${THEME.spacing.ultra};
  gap: ${THEME.spacing.md};
`;

const LoadingText = styled(BodyShortMedium)`
  margin: 0;
  color: ${THEME.colors.textMuted};
`;

/**
 * ClientOnboardingList Component
 *
 * Displays a list of client onboardings with loading and empty states
 *
 * @param {Object} props - Component props
 * @param {Array} props.onboardings - Array of onboarding objects
 * @param {boolean} props.isLoading - Loading state
 * @param {Function} props.onContinue - Callback when continue button is clicked
 * @returns {JSX.Element} The client onboarding list component
 */
const ClientOnboardingList = ({ onboardings, isLoading, onContinue }) => {
  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner size="32px" />
        <LoadingText>Laster pågående kunderegistreringer...</LoadingText>
      </LoadingContainer>
    );
  }

  if (onboardings.length === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateIcon>📋</EmptyStateIcon>
        <EmptyStateTitle>
          Ingen kunder i registreringsmodus for øyeblikket
        </EmptyStateTitle>
        <EmptyStateMessage>
          Opprett en ny kunde for å komme i gang
        </EmptyStateMessage>
      </EmptyStateContainer>
    );
  }

  // Filter out completed onboardings
  const activeOnboardings = onboardings.filter(
    (onboarding) => !onboarding.isCompleted
  );

  if (activeOnboardings.length === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateIcon>📋</EmptyStateIcon>
        <EmptyStateTitle>
          Ingen kunder i registreringsmodus for øyeblikket
        </EmptyStateTitle>
        <EmptyStateMessage>
          Opprett en ny kunde for å komme i gang
        </EmptyStateMessage>
      </EmptyStateContainer>
    );
  }

  return (
    <ListContainer>
      {activeOnboardings.map((onboarding) => (
        <ClientOnboardingCard
          key={onboarding.id || onboarding.submissionId}
          onboarding={onboarding}
          onContinue={() => {
            // Use the id field as submissionId for client onboardings
            const submissionId = onboarding.submissionId || onboarding.id;
            onContinue(submissionId);
          }}
        />
      ))}
    </ListContainer>
  );
};

export default ClientOnboardingList;
